import React from "react";

import Alert from "@cloudscape-design/components/alert";
import Badge from "@cloudscape-design/components/badge";
import Box from "@cloudscape-design/components/box";
import Button from "@cloudscape-design/components/button";
import Preferences from "@cloudscape-design/components/collection-preferences";
import FormField from "@cloudscape-design/components/form-field";
import Grid from "@cloudscape-design/components/grid";
import Header from "@cloudscape-design/components/header";
import Input from "@cloudscape-design/components/input";
import Pagination from "@cloudscape-design/components/pagination";
import SpaceBetween from "@cloudscape-design/components/space-between";
import Table from "@cloudscape-design/components/table";

import WelcomeFreespinsRuleEditor from "./WelcomeFreespinsRuleEditor";

const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZE_STORAGE_KEY = "welcomeFreespins.perPage";
const PAGE_SIZE_OPTIONS = [
	{ value: 10, label: "10" },
	{ value: 30, label: "30" },
	{ value: 50, label: "50" },
	{ value: 100, label: "100" },
];
const MAX_PARTNER_UID = 2147483647;

const parsePartnerUid = (value) => {
	const rawValue = String(value).trim();
	if (!/^\d+$/.test(rawValue)) return null;

	const parsedValue = parseInt(rawValue, 10);
	if (!Number.isSafeInteger(parsedValue) || parsedValue <= 0 || parsedValue > MAX_PARTNER_UID) return null;

	return parsedValue;
};

const getErrorMessage = (error) => {
	if (typeof error === "string") return error;
	if (typeof error?.text === "string") return error.text;
	if (typeof error?.message === "string") return error.message;
	return "Unable to load Welcome Freespins rules.";
};

class WelcomeFreespins extends React.Component {
	constructor(props) {
		super(props);

		const storedPageSize = Number(localStorage.getItem(PAGE_SIZE_STORAGE_KEY));
		const pageSize = PAGE_SIZE_OPTIONS.some((option) => option.value === storedPageSize) ? storedPageSize : DEFAULT_PAGE_SIZE;
		this.listRequestId = 0;
		this.filterTimer = null;
		this.unmounted = false;
		this.state = {
			items: [],
			total: 0,
			offset: 0,
			pageSize,
			loading: true,
			error: "",
			filterInput: "",
			filterError: "",
			partnerUidFilter: null,
			editorVisible: false,
			editorPartnerUid: null,
		};
	}

	componentDidMount() {
		this.loadList();
	}

	componentWillUnmount() {
		if (this.filterTimer) clearTimeout(this.filterTimer);
		this.unmounted = true;
	}

	loadList = async (overrides = {}) => {
		const offset = Object.prototype.hasOwnProperty.call(overrides, "offset") ? overrides.offset : this.state.offset;
		const pageSize = Object.prototype.hasOwnProperty.call(overrides, "pageSize") ? overrides.pageSize : this.state.pageSize;
		const partnerUidFilter = Object.prototype.hasOwnProperty.call(overrides, "partnerUidFilter") ? overrides.partnerUidFilter : this.state.partnerUidFilter;
		const requestId = ++this.listRequestId;
		const payload = { limit: pageSize, offset };
		if (partnerUidFilter !== null) payload.partnerUid = partnerUidFilter;

		this.setState({ loading: true, error: "", offset, pageSize, partnerUidFilter });

		try {
			const response = await this.props.Interface.welcomeFreespinsSettingsList(payload);
			if (this.unmounted || requestId !== this.listRequestId) return;

			const items = Array.isArray(response?.items) ? response.items : [];
			const total = Number.isSafeInteger(response?.total) && response.total >= 0 ? response.total : 0;

			if (total > 0 && offset >= total) {
				const lastOffset = Math.floor((total - 1) / pageSize) * pageSize;
				this.loadList({ offset: lastOffset, pageSize, partnerUidFilter });
				return;
			}

			this.setState({ items, total, offset: total === 0 ? 0 : offset, loading: false, error: "" });
		} catch (error) {
			if (this.unmounted || requestId !== this.listRequestId) return;
			this.setState({ items: [], total: 0, loading: false, error: getErrorMessage(error) });
		}
	};

	updatePageSize = (pageSize) => {
		if (!PAGE_SIZE_OPTIONS.some((option) => option.value === pageSize)) return;

		localStorage.setItem(PAGE_SIZE_STORAGE_KEY, pageSize);
		this.loadList({ offset: 0, pageSize });
	};

	updateFilter = (value) => {
		if (this.filterTimer) clearTimeout(this.filterTimer);
		this.listRequestId += 1;
		this.setState({ filterInput: value, filterError: "", loading: false });

		this.filterTimer = setTimeout(() => {
			const rawValue = value.trim();
			if (!rawValue) {
				this.loadList({ offset: 0, partnerUidFilter: null });
				return;
			}

			const partnerUid = parsePartnerUid(rawValue);
			if (!partnerUid) {
				this.setState({ filterError: "Enter one positive partner UID." });
				return;
			}

			this.loadList({ offset: 0, partnerUidFilter: partnerUid });
		}, 400);
	};

	openCreate = () => {
		this.setState({ editorVisible: true, editorPartnerUid: null });
	};

	openEditor = (partnerUid) => {
		this.setState({ editorVisible: true, editorPartnerUid: partnerUid });
	};

	closeEditor = () => {
		this.setState({ editorVisible: false, editorPartnerUid: null });
	};

	render() {
		const { items, total, offset, pageSize, loading, error, filterInput, filterError, partnerUidFilter, editorVisible, editorPartnerUid } = this.state;
		const currentPage = Math.floor(offset / pageSize) + 1;
		const pagesCount = Math.max(1, Math.ceil(total / pageSize));

		return (
			<SpaceBetween direction="vertical" size="m">
				<Header variant="h1" description="Manage current Welcome Freespins partner rules">
					Welcome Freespins
				</Header>

				{error ? (
					<Alert type="error" header="Unable to load rules" action={<Button onClick={() => this.loadList()}>Retry</Button>}>
						{error}
					</Alert>
				) : null}

				<Table
					stickyHeader
					trackBy="partnerUid"
					loading={loading}
					loadingText="Loading Welcome Freespins rules"
					items={items}
					columnDefinitions={[
						{
							id: "partnerUid",
							header: "Partner UID",
							cell: (item) => (
								<Button variant="link" onClick={() => this.openEditor(item.partnerUid)}>
									{item.partnerUid}
								</Button>
							),
						},
						{ id: "version", header: "Version", cell: (item) => item.version },
						{ id: "capMaximum", header: "Cap maximum", cell: (item) => item.capMaximum },
						{ id: "capUsed", header: "Cap used", cell: (item) => item.capUsed },
						{ id: "grantedCount", header: "Granted", cell: (item) => item.grantedCount },
						{
							id: "enabled",
							header: "Enabled",
							cell: (item) => (item.enabled ? <Badge color="green">Yes</Badge> : <Badge color="red">No</Badge>),
						},
						{ id: "game", header: "Game", cell: (item) => (item.gameName ? `${item.gameName} (#${item.gameId})` : `#${item.gameId}`) },
					]}
					filter={
						<Grid gridDefinition={[{ colspan: 3 }]}>
							<FormField label="Exact partner UID" errorText={filterError} stretch>
								<Input type="search" value={filterInput} inputMode="numeric" placeholder="Search by exact partner UID" onChange={({ detail }) => this.updateFilter(detail.value)} />
							</FormField>
						</Grid>
					}
					header={
						<Header
							variant="h2"
							description="Current Welcome Freespins rule for each partner"
							counter={`(${total})`}
							actions={
								<SpaceBetween direction="horizontal" size="xs">
									<Button onClick={() => this.loadList()} disabled={loading}>
										Refresh
									</Button>
									<Button iconName="add-plus" variant="primary" onClick={this.openCreate}>
										Create rule
									</Button>
								</SpaceBetween>
							}>
							Welcome Freespins Rules
						</Header>
					}
					pagination={
						<Pagination
							disabled={loading}
							currentPageIndex={currentPage}
							pagesCount={pagesCount}
							onChange={({ detail }) => this.loadList({ offset: (detail.currentPageIndex - 1) * pageSize })}
						/>
					}
					preferences={
						<Preferences
							cancelLabel="Cancel"
							confirmLabel="Confirm"
							pageSizePreference={{ title: "Welcome Freespins rules per page", options: PAGE_SIZE_OPTIONS }}
							preferences={{ pageSize }}
							title="Preferences"
							onConfirm={({ detail }) => this.updatePageSize(detail.pageSize)}
						/>
					}
					empty={
						<Box textAlign="center" color="inherit">
							<b>No Welcome Freespins rules found</b>
							<Box padding={{ bottom: "s" }} variant="p" color="inherit">
								{partnerUidFilter === null ? "Create the first partner rule." : "No rule exists for this exact partner UID."}
							</Box>
							<Button onClick={this.openCreate}>Create rule</Button>
						</Box>
					}
				/>

				<WelcomeFreespinsRuleEditor
					Interface={this.props.Interface}
					visible={editorVisible}
					partnerUid={editorPartnerUid}
					onDismiss={this.closeEditor}
					onMutation={() => this.loadList()}
				/>
			</SpaceBetween>
		);
	}
}

export default WelcomeFreespins;
